import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Check, Info } from 'lucide-react';
import { useTranslation } from '../../hooks/useTranslation';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  COMMON_INVOICE_TYPES,
  INVOICE_TYPE_CATEGORIES,
  getAllInvoiceTypeCodes,
  getInvoiceTypeInfo,
  getFormattedInvoiceTypeDisplay,
  getFormattedInvoiceTypeDisplayGerman,
  requiresOriginalReference,
  isCreditNote,
  isDebitNote,
  isCorrection,
  getInvoiceTypesByCategory,
  searchInvoiceTypes,
  type AllInvoiceType,
  type InvoiceTypeInfo,
  type InvoiceTypeCategory
} from '../../constants/invoiceTypes';

interface InvoiceTypeSelectorProps {
  value: string;
  onChange: (value: string) => void;
  className?: string;
  placeholder?: string;
  disabled?: boolean;
  error?: boolean;
  showGermanNames?: boolean;
  onTypeChange?: (typeInfo: { isCredit: boolean; isDebit: boolean; isCorrection: boolean; requiresReference: boolean }) => void;
  'data-testid'?: string;
}

export const InvoiceTypeSelector: React.FC<InvoiceTypeSelectorProps> = ({
  value,
  onChange,
  className = '',
  placeholder = 'Select invoice type...',
  disabled = false,
  error = false,
  showGermanNames = false,
  onTypeChange,
  'data-testid': testId
}) => {
  const { t } = useTranslation();
  const { language } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Get all invoice types organized by categories
  const organizedInvoiceTypes = React.useMemo(() => {
    const groups: { [key: string]: InvoiceTypeInfo[] } = {};
    
    // Add common invoice types first
    groups['Common'] = COMMON_INVOICE_TYPES
      .map(code => getInvoiceTypeInfo(code)!)
      .filter(Boolean);
    
    // Add invoice types by categories
    Object.entries(INVOICE_TYPE_CATEGORIES).forEach(([categoryName, codes]) => {
      groups[categoryName] = getInvoiceTypesByCategory(categoryName as InvoiceTypeCategory);
    });
    
    return groups;
  }, []);

  // Filter invoice types based on search term
  const filteredInvoiceTypes = React.useMemo(() => {
    if (!searchTerm) return organizedInvoiceTypes;
    
    const filtered = searchInvoiceTypes(searchTerm);
    return { 'Search Results': filtered };
  }, [searchTerm, organizedInvoiceTypes]);

  // Get flat list for keyboard navigation
  const allFilteredInvoiceTypes = React.useMemo(() => {
    return Object.values(filteredInvoiceTypes).flat();
  }, [filteredInvoiceTypes]);

  // Get display value for selected invoice type
  const getDisplayValue = (typeCode: string) => {
    if (!typeCode) return '';
    
    const type = getInvoiceTypeInfo(typeCode);
    if (!type) return typeCode;
    
    if (language === 'de' || showGermanNames) {
      return getFormattedInvoiceTypeDisplayGerman(typeCode);
    } else {
      return getFormattedInvoiceTypeDisplay(typeCode);
    }
  };

  // Handle outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
        setSelectedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Notify parent about type characteristics when value changes
  useEffect(() => {
    if (value && onTypeChange) {
      onTypeChange({
        isCredit: isCreditNote(value),
        isDebit: isDebitNote(value),
        isCorrection: isCorrection(value),
        requiresReference: requiresOriginalReference(value)
      });
    }
  }, [value, onTypeChange]);

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
        e.preventDefault();
        setIsOpen(true);
        setSelectedIndex(0);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < allFilteredInvoiceTypes.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : prev);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && allFilteredInvoiceTypes[selectedIndex]) {
          handleSelect(allFilteredInvoiceTypes[selectedIndex].code);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        setSearchTerm('');
        setSelectedIndex(-1);
        break;
    }
  };

  const handleSelect = (typeCode: string) => {
    onChange(typeCode);
    setIsOpen(false);
    setSearchTerm('');
    setSelectedIndex(-1);
  };

  const selectedInvoiceType = getInvoiceTypeInfo(value);

  return (
    <div className={`relative ${className}`} ref={dropdownRef} data-testid={testId}>
      <div
        className={`
          relative w-full cursor-pointer rounded-md border bg-white px-3 py-2 text-left 
          shadow-sm focus:outline-none focus:ring-1 review-input transition-colors
          ${error 
            ? 'border-red-300 focus:border-red-500 focus:ring-red-500' 
            : 'border-border-primary focus:border-indigo-500 focus:ring-indigo-500'
          }
          ${disabled ? 'bg-bg-secondary cursor-not-allowed' : 'hover:border-border-secondary'}
        `}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        onKeyDown={handleKeyDown}
        tabIndex={disabled ? -1 : 0}
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <span className={`block truncate ${!value ? 'text-text-tertiary' : ''}`}>
          {value ? getDisplayValue(value) : placeholder}
        </span>
        <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
          <ChevronDown 
            className={`h-5 w-5 text-text-muted transition-transform ${isOpen ? 'rotate-180' : ''}`}
            aria-hidden="true" 
          />
        </span>
      </div>

      {isOpen && (
        <div className="absolute z-50 mt-1 max-h-96 w-full overflow-auto rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none review-input">
          <div className="px-3 py-2 border-b border-border-primary">
            <input
              ref={inputRef}
              type="text"
              className="w-full border-0 p-0 text-sm placeholder-text-tertiary focus:ring-0"
              placeholder="Search invoice types..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setSelectedIndex(0);
              }}
              onKeyDown={handleKeyDown}
            />
          </div>

          {Object.entries(filteredInvoiceTypes).map(([categoryName, invoiceTypes]) => (
            invoiceTypes.length > 0 && (
              <div key={categoryName}>
                <div className="px-3 py-2 text-xs font-semibold text-text-tertiary uppercase bg-bg-secondary">
                  {categoryName}
                </div>
                {invoiceTypes.map((type, index) => {
                  const globalIndex = Object.entries(filteredInvoiceTypes)
                    .slice(0, Object.keys(filteredInvoiceTypes).indexOf(categoryName))
                    .reduce((acc, [, types]) => acc + types.length, 0) + index;
                  
                  const isSelected = selectedIndex === globalIndex;
                  const isCurrentValue = type.code === value;
                  const needsReference = requiresOriginalReference(type.code);
                  const isCredit = isCreditNote(type.code);
                  const isDebit = isDebitNote(type.code);
                  const isCorrect = isCorrection(type.code);
                  
                  return (
                    <div
                      key={type.code}
                      className={`
                        relative cursor-pointer select-none py-2 pl-3 pr-9 text-sm
                        ${isSelected ? 'bg-indigo-600 text-white' : 'text-text-primary'}
                        ${isCurrentValue ? 'font-semibold' : 'font-normal'}
                        hover:bg-indigo-600 hover:text-white
                      `}
                      onClick={() => handleSelect(type.code)}
                      role="option"
                      aria-selected={isCurrentValue}
                    >
                      <div className="flex items-center">
                        <span className={`font-mono mr-2 ${isSelected ? 'text-white' : 'text-text-tertiary'} min-w-[2.5rem]`}>
                          {type.code}
                        </span>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="truncate">
                              {language === 'de' || showGermanNames ? type.germanName : type.name}
                            </span>
                            {isCredit && (
                              <span className={`text-xs px-1 rounded ${isSelected ? 'bg-green-400 text-white' : 'bg-green-100 text-green-600'}`}>
                                Credit
                              </span>
                            )}
                            {isDebit && (
                              <span className={`text-xs px-1 rounded ${isSelected ? 'bg-red-400 text-white' : 'bg-red-100 text-red-600'}`}>
                                Debit
                              </span>
                            )}
                            {isCorrect && (
                              <span className={`text-xs px-1 rounded ${isSelected ? 'bg-orange-400 text-white' : 'bg-orange-100 text-orange-600'}`}>
                                Correction
                              </span>
                            )}
                          </div>
                          <div className={`text-xs ${isSelected ? 'text-indigo-200' : 'text-text-tertiary'}`}>
                            {type.description}
                          </div>
                          {needsReference && (
                            <div className={`text-xs flex items-center gap-1 mt-1 ${isSelected ? 'text-indigo-200' : 'text-orange-600'}`}>
                              <Info className="h-3 w-3" />
                              <span>Requires reference to original document</span>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {isCurrentValue && (
                        <span className="absolute inset-y-0 right-0 flex items-center pr-4">
                          <Check className="h-5 w-5" aria-hidden="true" />
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            )
          ))}

          {allFilteredInvoiceTypes.length === 0 && (
            <div className="px-3 py-2 text-sm text-text-tertiary">
              No invoice types found.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default InvoiceTypeSelector;